export type Priority = "Top" | "High" | "Medium";

export type JobStatus =
  | "Watching"
  | "Applied"
  | "Under consideration"
  | "Recruiter Screen"
  | "Scheduling Phone Screen"
  | "Phone Screen"
  | "Scheduling Full Loop"
  | "Interviewing"
  | "Offer"
  | "Rejected"
  | "Withdrawn"
  | "Ghosted"
  | "Not Moving Forward"
  | "Closed"
  | "Closed by Company";

export type Profile = {
  id: string;
  username: string;
  display_name: string | null;
  is_admin: boolean;
};

export type AgentSettings = {
  user_id: string;
  company_criteria: string;
  role_criteria: string;
  schedule_days: string[];
  email_alias: string;
  timezone: string;
  updated_at?: string;
};

export type Company = {
  id: string;
  user_id: string;
  external_id: string;
  priority: Priority;
  name: string;
  hq_location: string;
  company_type: string;
  ticker: string;
  employees: string;
  notes: string;
  careers_url: string;
  dashboard_url: string;
};

export type Job = {
  id: string;
  user_id: string;
  company_id: string;
  external_id: string;
  title: string;
  requisition: string;
  url: string;
  location: string;
  applied: boolean;
  application_date: string;
  status: JobStatus;
  notes: string;
  salary?: string;
  last_seen: string | null;
  closed_at: string | null;
  state_changed_at: string | null;
  company_status: string;
  created_at: string;
  updated_at: string;
};

export type ScanRun = {
  id: string;
  user_id: string;
  trigger: string;
  status: string;
  summary: string;
  report_markdown: string;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

/** Statuses for a job you have applied to, most advanced first. */
export const PIPELINE_STATUSES: JobStatus[] = [
  "Offer",
  "Interviewing",
  "Scheduling Full Loop",
  "Phone Screen",
  "Scheduling Phone Screen",
  "Recruiter Screen",
  "Under consideration",
  "Applied",
];

/** Applications that are no longer active. */
export const CLOSED_STATUSES: JobStatus[] = [
  "Closed",
  "Closed by Company",
  "Rejected",
  "Withdrawn",
  "Ghosted",
  "Not Moving Forward",
];

export type Stage = "Not applied" | "Applied" | "Interviews" | "Result" | "Closed";

/** Every status belongs to exactly one stage; stage is derived from status. */
export const STAGE_GROUPS: { stage: Stage; statuses: JobStatus[] }[] = [
  { stage: "Not applied", statuses: ["Watching"] },
  {
    stage: "Applied",
    statuses: ["Applied", "Under consideration", "Recruiter Screen"],
  },
  {
    stage: "Interviews",
    statuses: [
      "Scheduling Phone Screen",
      "Phone Screen",
      "Scheduling Full Loop",
      "Interviewing",
    ],
  },
  { stage: "Result", statuses: ["Offer"] },
  { stage: "Closed", statuses: CLOSED_STATUSES },
];

export const STAGES: Stage[] = STAGE_GROUPS.map((g) => g.stage);

export function stageOf(status: JobStatus): Stage {
  return STAGE_GROUPS.find((g) => g.statuses.includes(status))?.stage ?? "Not applied";
}

export const PRIORITIES: Priority[] = ["Top", "High", "Medium"];

export const JOB_STATUSES: JobStatus[] = STAGE_GROUPS.flatMap((g) => g.statuses);

export const WEEKDAYS = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
] as const;

export function isSeattleArea(location: string): boolean {
  const loc = location.toLowerCase();
  return (
    loc.includes("seattle") ||
    loc.includes("bellevue") ||
    loc.includes("redmond") ||
    loc.includes("kirkland") ||
    loc.includes("renton") ||
    loc.includes("issaquah") ||
    loc.includes("tacoma")
  );
}

export function isRemoteLocation(location: string): boolean {
  const loc = location.toLowerCase();
  return loc.includes("remote") || loc.includes("anywhere");
}
